import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { DeleteModal } from '@components/shared/TaModal/ConfirmationModal';
import { theme, themeBoxShadow } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { EditOutlined, HighlightOff } from '@mui/icons-material';
import { Pagination } from '@mui/lab';
import Button from '@mui/material/Button';

import { GeneralLoader } from '@components/GeneralLoader';
import { useDeleteFarm, useFarmStore, useFetchFarms } from '@pages/MyFarm/store';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { CropCard } from '../CropCard/CropCard';
import { AddCropSection } from './AddCropSection';
import { CropAdderCard } from './CropAdderCard';

const { t } = i18next;

export function mapUnit(unit: string): string {
	const units = {
		hectares: t('unitTypes.hectares'),
		m2: t('unitTypes.m2'),
		acres: t('unitTypes.acres'),
	};

	return (units as Record<string, string>)[unit] || unit;
}

export function MyFarmMainPage() {
	const pageSize = 9;
	const { navigateWithLanguage } = useLanguageNavigate();

	const [searchParams, setSearchParams] = useSearchParams();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedFarmId, setSelectedFarmId] = useState('');
	const [menuOpen, setMenuOpen] = useState(false);
	const { t } = useTranslation();
	const { farms } = useFarmStore();
	const { remove } = useDeleteFarm();
	const [pageNumber, setPageNumber] = useState(() =>
		parseInt(searchParams.get('page-number') || '1')
	);
	const { isLoading, refresh } = useFetchFarms(pageNumber, pageSize);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const updateSearchParams = useCallback(
		(page: string) => {
			const params = createSearchParams({ 'page-number': page.toString() }).toString();

			setSearchParams(params);
		},
		[setSearchParams]
	);

	const handleViewDetails = useCallback(
		(id: number) => {
			navigateWithLanguage(`/farmer/my-farm/details/${id}`);
		},
		[navigateWithLanguage]
	);

	const handleEdit = useCallback(
		(farmId: string) => {
			localStorage.removeItem('farmId');
			navigateWithLanguage(`/farmer/my-farm/edit/${farmId}`);
		},
		[navigateWithLanguage]
	);

	const handleRemove = useCallback(
		async (farmId: string) => {
			try {
				await remove(farmId);
				setIsModalOpen(false);
				refresh();
				showSnackbar(
					t('myFarm.removeSnackbarTitle'),
					t('myFarm.removeSnackbarDescription'),
					'success'
				);
			} catch (error) {
				setIsModalOpen(false);
				// @TODO fix error translation
				showSnackbar('Error', 'Something went wrong. Please try again.', 'error');
			}
		},
		[remove, refresh, showSnackbar, t]
	);

	const cancelRemoveFile = () => setIsModalOpen(false);

	const handleChange = useCallback(
		(_: React.ChangeEvent<any>, value: number) => {
			setPageNumber(value);
			updateSearchParams(value.toString());
		},
		[updateSearchParams]
	);

	useEffect(() => {
		const page = parseInt(searchParams.get('page-number') || '1');

		setPageNumber(page);
	}, [searchParams]);

	const cropCards = useMemo(() => {
		return (
			<div className="pb-0 sm:pb-[20px] h-auto sm:h-full flex flex-col justify-between mt-[20px]">
				<div className="grid xl:grid-cols-4 2xxl:grid-cols-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3  gap-x-[20px] gap-y-[35px]">
					<CropAdderCard />
					{farms?.data?.map((card: any) => (
						<React.Fragment key={card.id}>
							<CropCard
								menuOpen={menuOpen}
								image={card.crop.image_url}
								title={card.crop.name}
								location={card.crop_location_name || 'N/A'}
								size={`${card.crop_area} ${mapUnit(card.crop_unit)}`}
								menuOptions={[
									{
										label: t('myFarm.editCrop'),
										icon: <EditOutlined fontSize="small" />,
										onClick: () => handleEdit(card.id),
									},
									{
										label: t('myFarm.deleteCrop'),
										icon: <HighlightOff fontSize="small" color="error" />,
										onClick: () => {
											setSelectedFarmId(card.id);
											setIsModalOpen(true);
											setMenuOpen(false);
										},
									},
								]}
								onViewDetails={() => handleViewDetails(card.id)}
							/>
							<DeleteModal
								open={isModalOpen}
								onClose={cancelRemoveFile}
								title={t('myFarm.deleteCrop')}
								actions={
									<>
										<Button
											sx={{
												color: theme.palette.custom.darkGray,
												boxShadow: themeBoxShadow.primary,
											}}
											onClick={cancelRemoveFile}
										>
											{t('cancel')}
										</Button>
										<Button
											sx={{
												background: theme.palette.custom.brightRed,
												boxShadow: themeBoxShadow.primary,
											}}
											onClick={() => handleRemove(selectedFarmId)}
											variant="contained"
										>
											{t('yes')}
										</Button>
									</>
								}
							>
								<p className="text-secondary roboto-regular text-[16px] pt-[30px] pb-[10px]">
									{t('myFarm.removalConfirmationText')}
								</p>
							</DeleteModal>
						</React.Fragment>
					))}
				</div>
				<Pagination
					className="sm:!mt-0 !mt-[20px] "
					classes={{ root: 'flex items-center justify-end' }}
					count={farms?.totalPages}
					page={pageNumber}
					onChange={handleChange}
				/>
			</div>
		);
	}, [
		farms?.data,
		farms?.totalPages,
		handleChange,
		handleEdit,
		handleRemove,
		handleViewDetails,
		isModalOpen,
		menuOpen,
		pageNumber,
		selectedFarmId,
		t,
	]);

	return useMemo(() => {
		return isLoading ? (
			<GeneralLoader />
		) : (farms?.totalData ?? 0) !== 0 ? (
			cropCards
		) : (
			<AddCropSection />
		);
	}, [isLoading, farms?.totalData, cropCards]);
}

// ml-[320px] pt-[95px] pb-[20px] max-lg:!ml-0 min-h-max h-full !ml-[120px] !pt-[125px] pb-[96px]

// ml-[320px] pt-[95px] pb-[20px] max-lg:!ml-0 min-h-max h-full !ml-[120px] !pt-[125px] pb-[96px]
